
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from '../../lib/axios';
import Button from '../ui/button/Button';
import { Modal } from '../ui/modal';

interface Invitation {
  id?: number;
  token: string;
  store?: {
    id?: number;
    name?: string;
    shop?: string;
  };
  inviter?: {
    name?: string;
    email?: string;
  };
  role?: 'admin' | 'member' | string;
  email?: string;
  user_id?: number;
  message?: string;
  created_at?: string;
}

interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitations: Invitation[];
}

export default function InvitationModal({
  isOpen,
  onClose,
  invitations,
}: InvitationModalProps) {
  const { user, updateUser, refreshStores } = useAuth();
  const navigate = useNavigate();

  const [accepting, setAccepting] = useState<string | null>(null);
  const [skipping, setSkipping] = useState(false);
  const [tokenInvitation, setTokenInvitation] =
    useState<Invitation | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    console.log(
      '🔍 InvitationModal: Opened, checking for invitation data...'
    );

    const storedToken = localStorage.getItem('invitation_token');
    const pendingData = localStorage.getItem('pending_invitation_data');
    const acceptedData = localStorage.getItem('invitation_accepted');

    console.log('🔍 InvitationModal: Data check:', {
      storedToken: !!storedToken,
      pendingData: !!pendingData,
      acceptedData: !!acceptedData,
      invitationsLength: invitations.length,
      currentUserEmail: user?.email,
      currentUserId: user?.id,
    });

    if (pendingData && !invitations.length) {
      try {
        const parsedData = JSON.parse(pendingData);

        const isRecipient =
          parsedData.email === user?.email ||
          parsedData.user_id === user?.id;

        if (isRecipient) {
          setTokenInvitation({
            token: parsedData.token,
            store: parsedData.store,
            role: parsedData.role,
            message: parsedData.message,
            email: parsedData.email,
            user_id: parsedData.user_id,
          });
        } else {
          console.log('🚫 InvitationModal: Not recipient, closing');
          onClose();
        }
      } catch (error) {
        console.error(
          'Failed to parse pending invitation data:',
          error
        );
        onClose();
      }
    } else if (storedToken && !invitations.length) {
      axios
        .get(`/invitations/details/${storedToken}`)
        .then((response) => {
          if (response.data.success) {
            const invitation = response.data.invitation;

            const isRecipient =
              invitation.email === user?.email ||
              invitation.user_id === user?.id;

            if (isRecipient) {
              setTokenInvitation(invitation);
            } else {
              console.log(
                '🚫 InvitationModal: API - not recipient, closing'
              );
              localStorage.removeItem('invitation_token');
              onClose();
            }
          }
        })
        .catch((error) => {
          console.error(
            'Failed to fetch invitation details:',
            error
          );
          onClose();
        });
    }
  }, [isOpen, invitations.length, user?.email, user?.id]);

  const handleAcceptInvitation = async (
    invitation: Invitation
  ) => {
    setAccepting(invitation.token);

    try {
      const response = await axios.post('/invitations/accept', {
        token: invitation.token,
      });

      if (response.data.success) {
        localStorage.setItem(
          'invitation_data',
          JSON.stringify({
            store: invitation.store,
            role: invitation.role,
            inviter: invitation.inviter,
          })
        );

        localStorage.setItem(
          'invitation_accepted_time',
          Date.now().toString()
        );

        window.dispatchEvent(
          new CustomEvent('invitation_accepted', {
            detail: {
              accepted: true,
              storeName:
                invitation.store?.name ?? 'the store',
              message: `You've joined ${
                invitation.store?.name ?? 'the store'
              } as ${invitation.role}`,
            },
          })
        );

        await refreshStores();

        onClose();
        navigate('/scaleboard');
      } else {
        console.error(
          'Invitation acceptance failed:',
          response.data
        );
      }
    } catch (error: any) {
      console.error(
        'Error accepting invitation:',
        error
      );

      window.dispatchEvent(
        new CustomEvent('invitation_error', {
          detail: {
            message:
              error.response?.data?.message ||
              'Failed to accept invitation',
          },
        })
      );
    } finally {
      setAccepting(null);
    }
  };

  const handleSkipInvitations = () => {
    setSkipping(true);

    localStorage.removeItem('invitation_token');

    onClose();
    navigate('/on-boarding');

    setSkipping(false);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getRoleInfo = (role?: string) => {
    switch (role) {
      case 'admin':
        return {
          text: 'Admin',
          color:
            'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
        };

      case 'member':
        return {
          text: 'Member',
          color:
            'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
        };

      default:
        return {
          text: role ?? 'Unknown',
          color:
            'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300',
        };
    }
  };

  /*
   * Keep both invitation sources:
   * 1. invitations from props
   * 2. tokenInvitation from localStorage/API
   */
  const allInvitations: Invitation[] =
    invitations.length > 0
      ? invitations
      : tokenInvitation
        ? [tokenInvitation]
        : [];

  if (!isOpen || allInvitations.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="w-full max-w-lg mx-4"
    >
      <div
        className="
          w-full
          max-w-lg
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-6
          opacity-100
          shadow-xl
          dark:border-[#FFFFFF0F]
          dark:bg-[#111f33]
        "
      >
        {/* Header */}
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Store Invitations
          </h2>

          <button
            type="button"
            onClick={onClose}
            disabled={!!accepting || skipping}
            aria-label="Close invitation modal"
            className="
              rounded-md
              p-1.5
              text-slate-400
              transition-colors
              hover:bg-slate-100
              hover:text-slate-600
              disabled:cursor-not-allowed
              disabled:opacity-50
              dark:hover:bg-white/5
              dark:hover:text-slate-300
            "
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <p className="mb-5 text-sm text-slate-600 dark:text-slate-300">
          You've been invited to join the following stores
        </p>

        {/* Invitation cards */}
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {allInvitations.map((invitation, index) => {
            const roleInfo = getRoleInfo(invitation.role);
            const isAccepting =
              accepting === invitation.token;

            return (
              <div
                key={
                  invitation.id ??
                  invitation.token ??
                  index
                }
                className="
                  rounded-xl
                  border
                  border-slate-200
                  bg-white
                  p-4
                  shadow-sm
                  transition-shadow
                  hover:shadow-md
                  dark:border-[#FFFFFF0F]
                  dark:bg-white/[0.02]
                "
              >
                {/* Store name + role */}
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold text-slate-900 dark:text-white">
                    {invitation.store?.name ??
                      invitation.store?.shop ??
                      'Unknown Store'}
                  </span>

                  <span
                    className={`
                      inline-flex
                      items-center
                      rounded-full
                      px-2.5
                      py-0.5
                      text-xs
                      font-medium
                      ${roleInfo.color}
                    `}
                  >
                    {roleInfo.text}
                  </span>
                </div>

                {/* Shop URL */}
                {invitation.store?.shop && (
                  <p className="mb-1 break-all text-sm text-slate-500 dark:text-slate-400">
                    {invitation.store.shop}
                  </p>
                )}

                {/* Inviter */}
                {invitation.inviter?.name && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Invited by {invitation.inviter.name}
                    {invitation.inviter.email
                      ? ` (${invitation.inviter.email})`
                      : ''}
                  </p>
                )}

                {/* Date */}
                {invitation.created_at && (
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    {formatDate(invitation.created_at)}
                  </p>
                )}

                {/* Message */}
                {invitation.message && (
                  <div
                    className="
                      mt-3
                      rounded-lg
                      border
                      border-slate-200
                      bg-slate-50
                      px-3
                      py-2
                      text-sm
                      text-slate-600
                      dark:border-[#FFFFFF0F]
                      dark:bg-white/[0.03]
                      dark:text-slate-300
                    "
                  >
                    {invitation.message}
                  </div>
                )}

                {/* Accept button */}
                <div className="mt-4">
                  <Button
                    onClick={() =>
                      handleAcceptInvitation(invitation)
                    }
                    disabled={
                      !!accepting || skipping
                    }
                    className="
                      w-full
                      rounded-md
                    "
                  >
                    {isAccepting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span
                          className="
                            h-4
                            w-4
                            animate-spin
                            rounded-full
                            border-2
                            border-white/30
                            border-b-white
                          "
                        />
                        Accepting...
                      </span>
                    ) : (
                      'Accept Invitation'
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className="
            mt-5
            flex
            flex-col
            gap-3
            border-t
            border-slate-200
            pt-4
            dark:border-[#FFFFFF0F]
            sm:flex-row
            sm:items-center
            sm:justify-between
          "
        >
          <p className="text-sm text-slate-600 dark:text-slate-300">
            You can accept these later from your profile
          </p>

          <Button
            variant="outline"
            onClick={handleSkipInvitations}
            disabled={skipping || !!accepting}
            className="rounded-md"
          >
            {skipping ? 'Skipping...' : 'Skip for Now'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
